import { BaseEntity } from './common';

/**
 * Document Types
 * Types of documents that can be uploaded to person profiles
 */
export enum DocumentType {
  BIRTH_CERTIFICATE = 'birth-certificate',
  DEATH_CERTIFICATE = 'death-certificate',
  MARRIAGE_CERTIFICATE = 'marriage-certificate',
  DIVORCE_DECREE = 'divorce-decree',
  CENSUS_RECORD = 'census-record',
  MILITARY_RECORD = 'military-record',
  IMMIGRATION_RECORD = 'immigration-record',
  NATURALIZATION_RECORD = 'naturalization-record',
  LAND_DEED = 'land-deed',
  WILL_TESTAMENT = 'will-testament',
  PROBATE_RECORD = 'probate-record',
  OBITUARY = 'obituary',
  NEWSPAPER_ARTICLE = 'newspaper-article',
  LETTER = 'letter',
  DIARY_PAGE = 'diary-page',
  SCHOOL_RECORD = 'school-record',
  EMPLOYMENT_RECORD = 'employment-record',
  MEDICAL_RECORD = 'medical-record',
  PASSPORT = 'passport',
  PHOTO_ID = 'photo-id',
  OTHER = 'other'
}

/**
 * Document approval status
 */
export type DocumentApprovalStatus = 'pending' | 'approved' | 'rejected';

/**
 * Document entity
 * Represents a document uploaded to a person's profile
 */
export interface Document extends BaseEntity {
  // Association
  personId: string;                    // Person this document belongs to
  personName: string;                  // Cached for performance

  // Document metadata
  title: string;                       // Document title (e.g., "Birth Certificate")
  type: DocumentType;                  // Type of document
  description?: string;                // Optional description
  date?: string;                       // Document date (ISO format)
  location?: string;                   // Location associated with document
  source?: string;                     // Where document was obtained
  sourceUrl?: string;                  // URL to original source (if applicable)
  tags?: string[];                     // Custom tags for organization

  // File information
  fileName: string;                    // Original uploaded filename
  storageURL: string;                  // Firebase Storage URL
  thumbnailURL?: string;               // Thumbnail URL (if image)
  fileSize: number;                    // File size in bytes
  mimeType: string;                    // MIME type (e.g., application/pdf)

  // Transcription
  transcription?: string;              // Text transcription of document
  transcribedBy?: string;              // User who transcribed
  transcribedAt?: Date;                // When transcribed

  // Moderation
  approvalStatus: DocumentApprovalStatus;  // Approval status
  isApproved: boolean;                 // Quick check for approved status
  approvedBy?: string;                 // Admin who approved
  approvedAt?: Date;                   // When approved
  rejectionReason?: string;            // Reason for rejection

  // Metadata
  uploadedBy: string;                  // User who uploaded (same as createdBy)
  uploadedByName: string;              // Cached for performance
  updatedBy?: string;                  // Last user who updated
  updatedByName?: string;              // Cached for performance

  // Soft delete
  isDeleted: boolean;                  // Soft delete flag
  deletedBy?: string;                  // Who deleted it
  deletedAt?: Date;                    // When deleted
}

/**
 * Document metadata for upload
 */
export interface DocumentMetadata {
  title: string;
  type: DocumentType;
  description?: string;
  date?: string;
  location?: string;
  source?: string;
  sourceUrl?: string;
  tags?: string[];
}

/**
 * Document upload request
 */
export interface DocumentUploadRequest {
  file: File;
  personId: string;
  metadata: DocumentMetadata;
}

/**
 * Document filter options
 */
export interface DocumentFilters {
  type?: DocumentType;
  dateFrom?: string;
  dateTo?: string;
  uploadedBy?: string;
  tags?: string[];
  approvalStatus?: DocumentApprovalStatus;
  searchQuery?: string;
}

/**
 * Document statistics
 */
export interface DocumentStats {
  totalDocuments: number;
  documentsByType: Record<DocumentType, number>;
  pendingApproval: number;
  uploadedThisMonth: number;
  storageUsedMB: number;
}
